// Shared pieces for the Identity & Passes pages.
import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { ID_KINDS, STATUS_CHIP, VALIDITY, dmy, recName, printIdentity, getIdAdjust, setIdAdjust, ID_ADJUST_DEFAULT, printTestCard, ID_PAPERS, getIdPaper, setIdPaper } from "../lib/identity";
import { QrScanner } from "./QrScanButton";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Camera, Upload, ScanLine, Printer, X, ShieldCheck, CalendarHeart } from "lucide-react";
import { refreshDashTemples } from "./TempleDash";
import { toast } from "sonner";

let templesP = null;
export const refreshIdTemples = () => { templesP = null; };
const loadTemples = () => {
  if (!templesP) templesP = api.get("/meta/form-data").then((r) => r.data.temples || []).catch(() => { templesP = null; return []; });
  return templesP;
};

/** Temple list + selected temple (Super Admin chooses; others use their own temple). */
export function useIdTemple(user) {
  const [temples, setTemples] = useState([]);
  const [templeId, setTempleId] = useState(user.role === "SUPER_ADMIN" ? "" : user.temple_id);
  const reload = useCallback(() => {
    loadTemples().then((t) => {
      setTemples(t);
      if (user.role === "SUPER_ADMIN") setTempleId((cur) => cur || user.temple_id || t[0]?.temple_id || "");
    });
  }, []); // eslint-disable-line
  useEffect(reload, [reload]);
  return { temples, templeId, setTempleId, reload, temple: temples.find((t) => t.temple_id === templeId) || null };
}

export function TemplePicker({ user, temples, value, onChange, allowAll = false }) {
  if (user.role !== "SUPER_ADMIN") return null;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-lg border border-[#E5D9C5] px-3 text-sm bg-white" data-testid="id-temple">
      {allowAll && <option value="">All temples</option>}
      {temples.map((t) => <option key={t.temple_id} value={t.temple_id}>{t.temple_id} · {t.name}</option>)}
    </select>
  );
}

/** Departments / Locations of a temple (hidden locations left out of pickers). */
export function useIdMasters(templeId) {
  const [m, setM] = useState({ departments: [], locations: [] });
  const load = useCallback(() => {
    if (!templeId) return;
    Promise.all([api.get("/identity/masters/departments", { params: { temple_id: templeId } }), api.get("/identity/masters/locations", { params: { temple_id: templeId } })])
      .then(([d, l]) => setM({ departments: d.data, locations: l.data })).catch(() => {});
  }, [templeId]);
  useEffect(load, [load]);
  return m;
}

export function PageHead({ eyebrow, title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#E8590C] font-bold">{eyebrow}</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export const StatusChip = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${STATUS_CHIP[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
export const ValidityChip = ({ v }) => {
  const [label, cls] = VALIDITY[v] || [v, "bg-slate-200"];
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${cls}`}>{label}</span>;
};

/** Live camera capture or file upload; value is a JPEG data URL. */
export function PhotoField({ value, onChange, label = "Photo" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [on, setOn] = useState(false);
  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setOn(false); };
  useEffect(() => () => stop(), []);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 } } }); // full HD for a sharp printed photo
      streamRef.current = stream; setOn(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 80);
    } catch { toast.error("Camera not available. Allow camera permission, or upload a photo."); }
  };
  // Photos are kept large enough to print sharp on the card (26 x 31 mm photo box = ~600 x 730 px at 600 dpi).
  const QUALITY = 0.92;
  const drawTo = (src, sx, sy, sw, sh, w, h) => {
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const g = c.getContext("2d"); g.imageSmoothingEnabled = true; g.imageSmoothingQuality = "high";
    g.drawImage(src, sx, sy, sw, sh, 0, 0, w, h);
    return c.toDataURL("image/jpeg", QUALITY);
  };
  // Upload: keep the whole picture, longest side up to 1400 px.
  const shrink = (src, cb) => {
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 1400 / Math.max(img.width, img.height));
      cb(drawTo(img, 0, 0, img.width, img.height, Math.round(img.width * k), Math.round(img.height * k)));
    };
    img.src = src;
  };
  // Camera: centre portrait crop in the card photo's shape (5 : 6), up to 1000 x 1200 px.
  const capture = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    const vw = v.videoWidth, vh = v.videoHeight;
    const sh = Math.min(vh, vw * 6 / 5), sw = sh * 5 / 6;
    const k = Math.min(1, 1200 / sh);
    onChange(drawTo(v, (vw - sw) / 2, (vh - sh) / 2, sw, sh, Math.round(sw * k), Math.round(sh * k))); stop();
  };
  return (
    <div>
      <p className="text-xs font-semibold text-slate-700 mb-1">{label}</p>
      <div className="flex items-start gap-3">
        <div className="w-28 h-32 rounded-lg border-2 border-dashed border-[#E5D9C5] bg-[#FBF8F4] overflow-hidden flex items-center justify-center">
          {on ? <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            : value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <span className="text-[11px] text-slate-400">No photo</span>}
        </div>
        <div className="flex flex-col gap-2">
          {on ? <Button type="button" size="sm" onClick={capture} className="bg-maroon-700 hover:bg-maroon-800 text-white"><Camera className="w-4 h-4 mr-1" /> Take photo</Button>
            : <Button type="button" size="sm" variant="outline" onClick={start} data-testid="id-photo-camera"><Camera className="w-4 h-4 mr-1" /> Capture</Button>}
          <label className="inline-flex items-center h-9 px-3 rounded-md border border-input bg-white text-sm cursor-pointer hover:bg-slate-50">
            <Upload className="w-4 h-4 mr-1" /> Upload
            <input type="file" accept="image/*" className="hidden" data-testid="id-photo-upload" onChange={(e) => {
              const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
              const rd = new FileReader(); rd.onload = () => shrink(rd.result, onChange); rd.readAsDataURL(f);
            }} />
          </label>
          {value && !on && <button type="button" className="text-xs text-red-600 underline text-left" onClick={() => onChange("")}>Remove</button>}
        </div>
      </div>
    </div>
  );
}

/** Scan or type a pass / tag number: shows who it belongs to, and whether it is valid today. */
export function VerifyDialog({ open, onClose, temples, title = "Verify Pass / Tag" }) {
  const [code, setCode] = useState("");
  const [scan, setScan] = useState(true);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCode(""); setRes(null); setScan(true); } }, [open]);
  const check = async (c) => {
    const v = (c ?? code).trim(); if (!v) return;
    setBusy(true); setScan(false);
    try {
      const r = await api.get("/identity/verify", { params: { code: v } });
      const full = await api.get(`/identity/${r.data.kind}/${r.data.record.id}`);
      setRes({ ...r.data, record: full.data });
    } catch (e) { toast.error(fmtErr(e)); setRes(null); } finally { setBusy(false); }
  };
  const r = res?.record;
  const temple = r && (temples || []).find((t) => t.temple_id === r.temple_id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="id-verify-dialog">
        <DialogHeader><DialogTitle className="font-heading text-maroon-900 flex items-center gap-2"><ScanLine className="w-5 h-5 text-[#E8590C]" /> {title}</DialogTitle></DialogHeader>
        {!r && (
          <>
            {scan && open && <QrScanner className="rounded-lg min-h-[240px]" onScan={(t) => { setCode(t); check(t); }} onError={() => setScan(false)} />}
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-1">Or enter the pass number / tag serial</p>
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()} placeholder="DTY-T001-000001 or ssd0000001" className="font-mono" data-testid="id-verify-input" autoFocus />
              <Button onClick={() => check()} disabled={busy || !code.trim()} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="id-verify-go">Check</Button>
            </div>
          </>
        )}
        {r && (
          <div className="space-y-3" data-testid="id-verify-result">
            <div className={`rounded-xl p-3 text-white text-center ${r.validity === "VALID" ? "bg-green-600" : r.validity === "EXPIRED" || r.validity === "REJECTED" ? "bg-red-600" : "bg-amber-500"}`}>
              <ShieldCheck className="w-7 h-7 mx-auto" />
              <p className="font-extrabold text-lg">{(VALIDITY[r.validity] || [r.validity])[0]}</p>
              <p className="text-xs opacity-90">{res.label} · valid {dmy(r.valid_from)} to {dmy(r.valid_to)}</p>
            </div>
            <div className="flex gap-3">
              {r.photo ? <img src={r.photo} alt="" className="w-24 h-28 rounded-lg object-cover border" /> : <div className="w-24 h-28 rounded-lg bg-slate-100 flex items-center justify-center text-xs text-slate-400">No photo</div>}
              <div className="text-sm space-y-0.5 min-w-0">
                <p className="font-bold text-maroon-900 text-base">{recName(r)}</p>
                <p className="font-mono text-xs">{res.kind === "children" ? r.tag_serial : r.pass_no}</p>
                {r.vehicle_number && <p className="font-mono font-bold">{r.vehicle_number}</p>}
                {r.designation && <p>{r.designation}</p>}
                {r.department_name && <p>{r.department_name}</p>}
                {r.location_name && <p>{r.location_name}</p>}
                {r.father_name && <p>Father: {r.father_name}{r.mother_name ? ` · Mother: ${r.mother_name}` : ""}</p>}
                <p className="font-semibold">📞 {r.mobile}</p>
                {r.district && <p>{r.district}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setRes(null); setCode(""); setScan(true); }}><ScanLine className="w-4 h-4 mr-1" /> Scan another</Button>
              <Button variant="outline" onClick={() => printIdentity(res.kind, r, temple)}><Printer className="w-4 h-4 mr-1" /> Print</Button>
              <Button variant="ghost" onClick={onClose}><X className="w-4 h-4 mr-1" /> Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Fetch the full records (photo + QR) and print them. */
export async function fetchAndPrint(kind, rows, temples) {
  try {
    const full = await Promise.all(rows.map((r) => api.get(`/identity/${kind}/${r.id}`).then((x) => x.data)));
    const byTemple = full[0]?.temple_id;
    printIdentity(kind, full, (temples || []).find((t) => t.temple_id === byTemple));
  } catch (e) { toast.error(fmtErr(e)); }
}

export const KIND_LIST = Object.entries(ID_KINDS);

/** Super Admin / Temple Admin: event name (above the photo), signing officer and signature printed on the ID cards. */
export function EventNameEditor({ templeId, temples, onSaved }) {
  const t = (temples || []).find((x) => x.temple_id === templeId);
  const blank = { event_name: "", sign_label: "", signature: "" };
  const [v, setV] = useState(blank);
  const [orig, setOrig] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const x = { event_name: t?.id_event_name || "", sign_label: t?.id_sign_label || "", signature: t?.id_signature || "" };
    setV(x); setOrig(x);
  }, [t]);
  if (!templeId) return null;
  const changed = JSON.stringify(v) !== JSON.stringify(orig);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/identity/settings", { temple_id: templeId, ...v });
      refreshIdTemples(); refreshDashTemples(); setOrig(v);
      toast.success("Card settings saved - used on every card printed from now on");
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const pickSig = (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Signature picture too large (max 500 KB)"); return; }
    const rd = new FileReader();
    rd.onload = () => setV((x) => ({ ...x, signature: rd.result }));
    rd.readAsDataURL(file);
  };
  return (
    <div className="rounded-xl border border-[#F2DCCB] bg-white p-3" data-testid="id-event-editor">
      <p className="text-xs font-bold uppercase tracking-wider text-[#C2410C] mb-2 flex items-center gap-1.5"><CalendarHeart className="w-4 h-4" /> ID card settings (this temple)</p>
      <div className="grid md:grid-cols-[2fr_1.3fr_1.4fr_auto] gap-3 items-end">
        <div>
          <p className="text-xs font-semibold text-slate-700">Event name <span className="font-normal text-slate-500">(above the photo)</span></p>
          <Input value={v.event_name} maxLength={80} onChange={(e) => setV({ ...v, event_name: e.target.value })} placeholder="e.g. Mukkoti Ekadasi 2026" data-testid="id-event-name" />
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">Signing officer <span className="font-normal text-slate-500">(under the signature)</span></p>
          <Input value={v.sign_label} maxLength={60} list="id-sign-options" onChange={(e) => setV({ ...v, sign_label: e.target.value })} placeholder="e.g. District Collector" data-testid="id-sign-label" />
          <datalist id="id-sign-options">
            {["District Collector", "Revenue Divisional Officer (RDO)", "Superintendent of Police (SP)", "Executive Officer", "Joint Collector", "Tahsildar"].map((o) => <option key={o} value={o} />)}
          </datalist>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">Signature picture</p>
          <div className="flex items-center gap-2 h-10">
            {v.signature
              ? <img src={v.signature} alt="signature" className="h-10 max-w-[110px] object-contain border rounded bg-white" data-testid="id-sign-preview" />
              : <span className="text-[11px] text-slate-400">None</span>}
            <label className="text-xs text-maroon-700 underline cursor-pointer">{v.signature ? "Replace" : "Upload"}
              <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="id-sign-upload" onChange={(e) => { pickSig(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {v.signature && <button type="button" className="text-xs text-red-600 underline" onClick={() => setV({ ...v, signature: "" })}>Remove</button>}
          </div>
        </div>
        <Button onClick={save} disabled={busy || !changed} className="bg-[#E8590C] hover:bg-[#C2410C] text-white" data-testid="id-event-save">Save</Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">A department can have its own signing officer and signature (Departments page) - e.g. SP for Police passes.</p>
    </div>
  );
}

/** Per-PC 4x6 card printer settings: move / size, test print, and the print dialog settings to use. */
export function CardPrintSettings({ temple }) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState(getIdAdjust());
  const [paper, setPaper] = useState(getIdPaper());
  const paperText = paper === "a4c" ? "A4 210 x 297 mm (the card is fed in the centre - nothing else to set)" : paper === "4x6" ? "4 x 6 in (10 x 15 cm)" : paper === "a6" ? "A6 (105 x 148 mm)" : "99 x 132 mm - in the printer's Printing Preferences create a User-Defined paper exactly 99 x 132 mm";
  const save = (n) => { setA(n); setIdAdjust(n); };
  const num = (k, step, min, max, unit) => (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="sm" onClick={() => save({ ...a, [k]: Math.max(min, Math.round((Number(a[k]) - step) * 10) / 10) })}>−</Button>
      <Input type="number" value={a[k]} onChange={(e) => save({ ...a, [k]: e.target.value === "" ? 0 : Math.min(max, Math.max(min, Number(e.target.value))) })} className="w-20 text-center" />
      <Button type="button" variant="outline" size="sm" onClick={() => save({ ...a, [k]: Math.min(max, Math.round((Number(a[k]) + step) * 10) / 10) })}>+</Button>
      <span className="text-xs text-slate-500">{unit}</span>
    </div>
  );
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="id-card-print-settings"><Printer className="w-4 h-4 mr-1" /> Card print settings</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="id-card-print-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">ID card printing (this PC)</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">Card size in the printer</Label>
            <select value={paper} onChange={(e) => { setPaper(e.target.value); setIdPaper(e.target.value); }} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="id-paper">
              {ID_PAPERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-1">
            <p className="font-bold">In the Chrome print window, choose once:</p>
            <p>• <b>Destination</b>: your card printer</p>
            <p>• <b>More settings → Paper size</b>: <b>{paperText}</b></p>
            <p>• <b>Layout</b>: Portrait · <b>Margins</b>: None · <b>Scale</b>: Default (100)</p>
            <p>• <b>Background graphics</b>: ticked</p>
            <p className="text-xs text-amber-800">Direct-print counter shortcuts remember the paper of their own counter (e.g. landscape tickets) - print ID cards from normal Chrome, or set the paper above once in that shortcut.</p>
          </div>
          <div className="space-y-3">
            <div><Label className="text-xs">Move down (+) / up (−)</Label>{num("y", 1, -40, 40, "mm")}</div>
            <div><Label className="text-xs">Move right (+) / left (−)</Label>{num("x", 1, -20, 20, "mm")}</div>
            <div><Label className="text-xs">Size</Label>{num("s", 1, 80, 110, "%")}</div>
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="ghost" onClick={() => save({ ...ID_ADJUST_DEFAULT })}>Reset</Button>
            <Button onClick={() => printTestCard(temple)} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="id-test-print"><Printer className="w-4 h-4 mr-1" /> Print test card</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
